// hooks/useCrewEfficiency.js
import { useMemo } from 'react';
import moment from 'moment';

/**
 * Calculate comprehensive crew efficiency metrics
 * @param {Array} weeklyRoutes - Weekly routes data
 * @param {Array} crews - Available crews
 * @param {Date} selectedDate - Currently selected date
 * @returns {Object} Crew efficiency metrics and insights
 */
export const useCrewEfficiency = (weeklyRoutes, crews, selectedDate) => {
  return useMemo(() => {
    if (!weeklyRoutes || !crews || weeklyRoutes.length === 0) {
      return {
        crewMetrics: {},
        rankings: {
          efficiency: [],
          workload: [],
          geographic: []
        },
        insights: {
          inefficientCrews: [],
          overworkedCrews: [],
          underutilizedCrews: [],
          territoryIssues: []
        },
        summary: {
          averageEfficiency: 0,
          totalOvertime: 0,
          avgMilesPerStop: 0,
          totalCrews: 0,
          crewsWorking: 0
        },
        recommendations: {
          immediate: [],
          optimization: []
        }
      };
    }

    // Initialize metrics for each crew
    const crewMetricsMap = {};
    crews.forEach(crew => {
      crewMetricsMap[crew._id] = {
        crewId: crew._id,
        crewName: crew.name,
        crewColor: crew.color,
        efficiency: {
          totalServiceTime: 0,
          totalDriveTime: 0,
          servicePercent: 0,
          drivePercent: 0,
          score: 'N/A',
          trend: 'stable'
        },
        workload: {
          weeklyHours: 0,
          dailyHours: {},
          dailyAverage: 0,
          overtime: 0,
          utilization: 0,
          daysWorked: 0
        },
        geographic: {
          totalMiles: 0,
          totalStops: 0,
          avgMilesPerStop: 0,
          avgMilesBetweenStops: 0,
          longestDrive: 0,
          territorySprawl: 'compact',
          outOfZoneStops: 0
        },
        performance: {
          stopsCompleted: 0,
          estimatedVsActual: 0,
          consistency: 0
        }
      };
    });

    // Process each day's routes
    const dailyEfficiencyTrends = {};
    weeklyRoutes.forEach(dayRoute => {
      const dayName = dayRoute.day;
      dailyEfficiencyTrends[dayName] = {};

      dayRoute.routes?.forEach(route => {
        const crewId = route.crewId;
        if (!crewMetricsMap[crewId]) return;

        const metrics = crewMetricsMap[crewId];

        // Calculate route metrics
        const serviceTime = (route.totalServiceTime || 0) / 60; // Convert to hours
        const driveTime = (route.totalDriveTime || 0) / 60; // Convert to hours
        const totalTime = serviceTime + driveTime;
        const stops = route.stops?.length || 0;
        const miles = route.totalMilesDriven || 0;

        // Update efficiency metrics
        metrics.efficiency.totalServiceTime += serviceTime;
        metrics.efficiency.totalDriveTime += driveTime;

        // Update workload metrics
        metrics.workload.weeklyHours += totalTime;
        metrics.workload.dailyHours[dayName] = totalTime;
        if (totalTime > 0) metrics.workload.daysWorked++;

        // Update geographic metrics
        metrics.geographic.totalMiles += miles;
        metrics.geographic.totalStops += stops;

        // Track daily efficiency for trends
        if (totalTime > 0) {
          dailyEfficiencyTrends[dayName][crewId] = (serviceTime / totalTime) * 100;
        }

        // Find longest single drive segment
        route.stops?.forEach((stop, index) => {
          if (index > 0 && stop.estimatedDriveDuration) {
            metrics.geographic.longestDrive = Math.max(
              metrics.geographic.longestDrive,
              stop.estimatedDriveDuration
            );
          }
        });

        // Update performance metrics
        metrics.performance.stopsCompleted += stops;
      });
    });

    // Calculate final metrics and scores for each crew
    Object.values(crewMetricsMap).forEach(metrics => {
      // Calculate efficiency percentages
      const totalTime = metrics.efficiency.totalServiceTime + metrics.efficiency.totalDriveTime;
      if (totalTime > 0) {
        metrics.efficiency.servicePercent = Math.round(
          (metrics.efficiency.totalServiceTime / totalTime) * 100
        );
        metrics.efficiency.drivePercent = 100 - metrics.efficiency.servicePercent;

        // Assign efficiency score (A-F)
        if (metrics.efficiency.servicePercent >= 75) metrics.efficiency.score = 'A';
        else if (metrics.efficiency.servicePercent >= 65) metrics.efficiency.score = 'B';
        else if (metrics.efficiency.servicePercent >= 55) metrics.efficiency.score = 'C';
        else if (metrics.efficiency.servicePercent >= 45) metrics.efficiency.score = 'D';
        else metrics.efficiency.score = 'F';
      }

      // Calculate workload metrics
      if (metrics.workload.daysWorked > 0) {
        metrics.workload.dailyAverage = 
          Math.round((metrics.workload.weeklyHours / metrics.workload.daysWorked) * 10) / 10;
        
        // Calculate overtime (assuming 8 hour days)
        metrics.workload.overtime = Math.max(
          0,
          metrics.workload.weeklyHours - (metrics.workload.daysWorked * 8)
        );

        // Calculate utilization (assuming 40 hour week max)
        metrics.workload.utilization = Math.min(
          100,
          Math.round((metrics.workload.weeklyHours / 40) * 100)
        );
      }

      // Calculate geographic metrics
      if (metrics.geographic.totalStops > 0) {
        metrics.geographic.avgMilesPerStop = 
          Math.round((metrics.geographic.totalMiles / metrics.geographic.totalStops) * 10) / 10;

        // Calculate average miles between consecutive stops
        if (metrics.geographic.totalStops > 1) {
          metrics.geographic.avgMilesBetweenStops = 
            Math.round((metrics.geographic.totalMiles / (metrics.geographic.totalStops - 1)) * 10) / 10;
        }

        // Determine territory sprawl
        if (metrics.geographic.avgMilesPerStop > 5) {
          metrics.geographic.territorySprawl = 'excessive';
        } else if (metrics.geographic.avgMilesPerStop > 3) {
          metrics.geographic.territorySprawl = 'moderate';
        } else {
          metrics.geographic.territorySprawl = 'compact';
        }
      }

      // Calculate efficiency trend (compare to previous week if available)
      // For now, we'll mark as improving if efficiency is good
      if (metrics.efficiency.servicePercent > 70) {
        metrics.efficiency.trend = 'improving';
      } else if (metrics.efficiency.servicePercent < 50) {
        metrics.efficiency.trend = 'declining';
      }
    });

    // Create rankings
    const activeCrewMetrics = Object.values(crewMetricsMap)
      .filter(m => m.workload.weeklyHours > 0);

    const rankings = {
      efficiency: [...activeCrewMetrics]
        .sort((a, b) => b.efficiency.servicePercent - a.efficiency.servicePercent)
        .map((m, index) => ({
          rank: index + 1,
          crewId: m.crewId,
          crewName: m.crewName,
          value: m.efficiency.servicePercent,
          score: m.efficiency.score
        })),
      
      workload: [...activeCrewMetrics]
        .sort((a, b) => b.workload.weeklyHours - a.workload.weeklyHours)
        .map((m, index) => ({
          rank: index + 1,
          crewId: m.crewId,
          crewName: m.crewName,
          value: m.workload.weeklyHours,
          overtime: m.workload.overtime
        })),
      
      geographic: [...activeCrewMetrics]
        .sort((a, b) => a.geographic.avgMilesPerStop - b.geographic.avgMilesPerStop)
        .map((m, index) => ({
          rank: index + 1,
          crewId: m.crewId,
          crewName: m.crewName,
          value: m.geographic.avgMilesPerStop,
          totalMiles: m.geographic.totalMiles
        }))
    };

    // Identify insights and issues
    const insights = {
      inefficientCrews: activeCrewMetrics
        .filter(m => m.efficiency.servicePercent < 50)
        .map(m => ({
          crewId: m.crewId,
          crewName: m.crewName,
          efficiency: m.efficiency.servicePercent,
          recommendation: `Spending ${m.efficiency.drivePercent}% time driving. Consider route optimization.`
        })),
      
      overworkedCrews: activeCrewMetrics
        .filter(m => m.workload.overtime > 4)
        .map(m => ({
          crewId: m.crewId,
          crewName: m.crewName,
          overtime: Math.round(m.workload.overtime * 10) / 10,
          recommendation: `${m.workload.overtime.toFixed(1)} hours overtime. Consider redistributing workload.`
        })),
      
      underutilizedCrews: activeCrewMetrics
        .filter(m => m.workload.utilization < 60 && m.workload.daysWorked >= 3)
        .map(m => ({
          crewId: m.crewId,
          crewName: m.crewName,
          utilization: m.workload.utilization,
          recommendation: `Only ${m.workload.utilization}% utilized. Can take on more assignments.`
        })),
      
      territoryIssues: activeCrewMetrics
        .filter(m => m.geographic.territorySprawl === 'excessive')
        .map(m => ({
          crewId: m.crewId,
          crewName: m.crewName,
          avgMilesPerStop: m.geographic.avgMilesPerStop,
          recommendation: `Averaging ${m.geographic.avgMilesPerStop} miles per stop. Consider reassigning jobs to reduce travel.`
        }))
    };

    // Calculate summary statistics
    const summary = {
      averageEfficiency: activeCrewMetrics.length > 0
        ? Math.round(
            activeCrewMetrics.reduce((sum, m) => sum + m.efficiency.servicePercent, 0) / 
            activeCrewMetrics.length
          )
        : 0,
      
      totalOvertime: Math.round(
        activeCrewMetrics.reduce((sum, m) => sum + m.workload.overtime, 0) * 10
      ) / 10,
      
      avgMilesPerStop: activeCrewMetrics.length > 0
        ? Math.round(
            activeCrewMetrics.reduce((sum, m) => sum + m.geographic.avgMilesPerStop, 0) / 
            activeCrewMetrics.length * 10
          ) / 10
        : 0,
      
      crewsWorking: activeCrewMetrics.length,
      totalCrews: crews.length
    };

    // Generate actionable recommendations
    const recommendations = generateRecommendations(
      activeCrewMetrics,
      insights,
      summary
    );

    return {
      crewMetrics: crewMetricsMap,
      rankings,
      insights,
      summary,
      dailyTrends: dailyEfficiencyTrends,
      recommendations
    };
  }, [weeklyRoutes, crews, selectedDate]);
};

/**
 * Generate actionable recommendations based on metrics
 * @param {Array} activeCrewMetrics - Metrics for active crews
 * @param {Object} insights - Current insights
 * @param {Object} summary - Summary statistics
 * @returns {Object} Recommendations
 */
const generateRecommendations = (activeCrewMetrics, insights, summary) => {
  const recommendations = {
    immediate: [],
    optimization: []
  };

  // Check for crews that could swap territories
  if (insights.inefficientCrews.length > 0 && insights.underutilizedCrews.length > 0) {
    recommendations.immediate.push({
      type: 'workload_rebalance',
      priority: 'high',
      title: 'Rebalance Workload',
      description: `${insights.inefficientCrews.length} crews are inefficient while ${insights.underutilizedCrews.length} have capacity`,
      action: 'Consider reassigning jobs from inefficient to underutilized crews',
      potentialImpact: 'Could improve overall efficiency by 15-20%',
      affectedCrews: [
        ...insights.inefficientCrews.map(c => c.crewName),
        ...insights.underutilizedCrews.map(c => c.crewName)
      ]
    });
  }

  // Check for territory optimization opportunities
  if (insights.territoryIssues.length > 0) {
    const totalExcessMiles = insights.territoryIssues.reduce(
      (sum, issue) => sum + (issue.avgMilesPerStop - 3) * 20, // Assuming 20 stops per crew
      0
    );
    
    recommendations.optimization.push({
      type: 'territory_redesign',
      priority: 'medium',
      title: 'Optimize Job Assignments',
      description: `${insights.territoryIssues.length} crews have excessive travel distances`,
      action: 'Reassign jobs/change days to reduce travel',
      potentialImpact: `Save approximately ${Math.round(totalExcessMiles)} miles per week`,
      estimatedSavings: `$${Math.round(totalExcessMiles * 0.655)}` // IRS mileage rate
    });
  }

  // Check for overtime reduction opportunities
  if (summary.totalOvertime > 10) {
    recommendations.immediate.push({
      type: 'overtime_reduction',
      priority: 'high',
      title: 'Reduce Overtime Costs',
      description: `${summary.totalOvertime.toFixed(1)} total overtime hours this week`,
      action: 'Redistribute jobs from overworked crews or consider hiring',
      potentialImpact: `Save $${Math.round(summary.totalOvertime * 35 * 1.5)}`, // Assuming $35/hr * 1.5x
      affectedCrews: insights.overworkedCrews.map(c => c.crewName)
    });
  }

  // Check for efficiency improvements
  if (summary.averageEfficiency < 65) {
    recommendations.optimization.push({
      type: 'route_optimization',
      priority: 'high',
      title: 'Improve Route Efficiency',
      description: `Average efficiency is only ${summary.averageEfficiency}%`,
      action: 'Run route optimization algorithm to reduce drive time',
      potentialImpact: 'Could save 30-45 minutes per crew per day',
      quickWin: true
    });
  }

  // Add day-specific recommendations
  const busyDays = activeCrewMetrics.filter(c => 
    Object.values(c.workload.dailyHours).some(h => h > 9)
  );
  
  if (busyDays.length > 3) {
    recommendations.optimization.push({
      type: 'schedule_leveling',
      priority: 'medium',
      title: 'Level Weekly Schedule',
      description: 'Some days are significantly busier than others',
      action: 'Move non-urgent jobs from busy days to lighter days',
      potentialImpact: 'Reduce peak day stress and overtime'
    });
  }

  return recommendations;
};

/**
 * Get efficiency recommendations for a specific crew
 * @param {Object} crewMetrics - Metrics for a specific crew
 * @returns {Array} Array of recommendations
 */
export const getCrewRecommendations = (crewMetrics) => {
  const recommendations = [];

  // Efficiency recommendations
  if (crewMetrics.efficiency.servicePercent < 50) {
    recommendations.push({
      type: 'efficiency',
      priority: 'high',
      message: 'Low service efficiency - too much time spent driving',
      action: 'Consider route optimization or job reassignment'
    });
  }

  // Workload recommendations
  if (crewMetrics.workload.overtime > 8) {
    recommendations.push({
      type: 'workload',
      priority: 'high',
      message: 'Excessive overtime hours',
      action: 'Redistribute some assignments to underutilized crews'
    });
  } else if (crewMetrics.workload.utilization < 50) {
    recommendations.push({
      type: 'workload',
      priority: 'medium',
      message: 'Low utilization rate',
      action: 'This crew has capacity for additional assignments'
    });
  }

  // Geographic recommendations
  if (crewMetrics.geographic.territorySprawl === 'excessive') {
    recommendations.push({
      type: 'geographic',
      priority: 'high',
      message: 'Jobs too spread out',
      action: 'Reassign distant stops to closer crews'
    });
  }

  if (crewMetrics.geographic.longestDrive > 30) {
    recommendations.push({
      type: 'geographic',
      priority: 'medium',
      message: `Long drive segments (${crewMetrics.geographic.longestDrive} min)`,
      action: 'Look for stops that can be combined or reassigned'
    });
  }

  return recommendations;
};